import { isEmpty } from 'lodash';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { UseFormHandleSubmit, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { BomPartType } from 'bom/enums';
import { IElement } from 'bom/types';
import { Form, IManagedNote } from 'core/components';
import { AlternateVersionHelper } from 'core/helpers';
import { useFocusInputFunctionality } from 'core/hooks';
import { useFormContext } from 'core/states';
import { enterPress } from 'core/utils';
import { IJobPart } from 'jobs/types';
import { PartSection, PricingSection } from 'parts/components';
import { useGetUoms } from 'parts/hooks';
import { IUom } from 'parts/types';

import { usePartValidation, usePriceCalculation } from './hooks';
import { NotesSection, QuantitySection } from './sections';
import { IUpsertLineFormData } from './types';
import { useNotes } from './useNotes';

interface IProps {
  jobId: number;
  jobPart?: IJobPart;
  element?: IElement;
  isOpen: boolean;
  onClose: (reset: UseFormReset<IUpsertLineFormData>) => void;
  onSubmit: (reset: UseFormReset<IUpsertLineFormData>) => (data: IUpsertLineFormData) => void;
}

export const UpsertLineFormFields = ({
  jobId,
  jobPart,
  element,
  isOpen,
  onClose,
  onSubmit,
}: IProps) => {
  const [uomOptions, setUomOptions] = useState<IUom[]>(
    jobPart?.allowedUoms.map((uom: string) => ({ id: uom, name: uom })) || []
  );
  const [verifiedPart, setVerifiedPart] = useState(Boolean(jobPart?.partArr?.verified));

  const { t } = useTranslation();

  const { setValue, getValues, reset, watch, dirtyFields } = useFormContext();
  const { setFocusElementName } = useFocusInputFunctionality();

  const { user } = useAuthUser();
  const { records: uoms } = useGetUoms({ filters: { bomDisplay: 1 } }, isOpen && !jobPart?.id);

  const isEditComponent = useMemo(
    () => !!jobPart?.id && jobPart?.type === BomPartType.COMPONENT,
    [jobPart?.id, jobPart?.type]
  );

  const {
    standardCost,
    purchaseCost,
    setStandardCost,
    setPurchaseCost,
    onBlurQuantity,
    onChangeUom,
    resetPrices,
  } = usePriceCalculation({ jobId, jobPart, getValues, watch, dirtyFields });

  const { isValidating, onChangePartNumber, onKeyDownPartNumber, onChangeManufacturer } =
    usePartValidation({
      setValue,
      getValues,
      reset,
      watch,
      setUomOptions,
      setVerifiedPart,
      setStandardCost,
      setPurchaseCost,
      isEditComponent,
    });

  const isEdit = useMemo(() => !!jobPart?.id, [jobPart?.id]);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const [partValue, manufacturerValue, descriptionValue, notesValue] = watch([
    'part',
    'manufacturer',
    'description',
    'notes',
  ]);

  const emptyPartValues = useMemo(
    () => isEmpty(partValue || manufacturerValue || descriptionValue),
    [partValue, manufacturerValue, descriptionValue]
  );

  const disabledQuantity = useMemo(() => isEdit && !element?.id, [isEdit, element?.id]);

  const { onAddNote, onDeleteNote, onEditNote } = useNotes({
    jobId: jobPart?.bomId || jobId,
    componentId: jobPart?.componentId || 0,
    notes: notesValue,
    setNotes: (value: IManagedNote[]) => setValue('notes', value),
    isEdit,
  });

  useEffect(() => {
    if (isOpen) {
      setFocusElementName('part');
    }
  }, [isOpen, setFocusElementName]);

  useEffect(() => {
    if (uoms.length > 0) {
      setUomOptions(uoms);
    }
  }, [uoms, setUomOptions]);

  useEffect(() => {
    if (!emptyPartValues && !watch('uom') && uomOptions.length > 0) {
      setValue('uom', uomOptions?.[0]?.name);
    }
  }, [emptyPartValues, uomOptions, watch, setValue]);

  const onKeyDownQuantity = (
    event: KeyboardEvent<HTMLInputElement>,
    handleSubmit: UseFormHandleSubmit<IUpsertLineFormData>
  ) => {
    enterPress(event, handleSubmit(onSubmit(reset)));
  };

  const handleOnClose = (reset: UseFormReset<IUpsertLineFormData>) => {
    setVerifiedPart(false);
    setUomOptions(uoms);
    resetPrices();
    onClose(reset);
  };

  return (
    <Form
      isOpen={isOpen}
      onClose={handleOnClose}
      title={isEdit ? t('titleUpdateLine') : t('titleAddLine')}
      submitBtnLabel={t('buttonLabelSave')}
      onSubmit={onSubmit}
      level={0}
    >
      <PartSection
        onChangePartNumber={onChangePartNumber}
        onKeyDownPartNumber={onKeyDownPartNumber}
        onChangeManufacturer={onChangeManufacturer}
        isValidating={isValidating}
        verifiedPart={verifiedPart}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <QuantitySection
        onBlurQuantity={onBlurQuantity}
        onKeyDownQuantity={onKeyDownQuantity}
        onChangeUom={onChangeUom}
        uomOptions={uomOptions}
        disabledQuantity={disabledQuantity}
        isValidating={isValidating}
        emptyPartValues={emptyPartValues}
      />

      <PricingSection purchaseCost={purchaseCost} standardCost={standardCost} />

      <NotesSection
        jobId={jobId}
        jobPart={jobPart}
        element={element}
        notesValue={notesValue}
        onDeleteNote={onDeleteNote}
        onEditNote={onEditNote}
        onAddNote={onAddNote}
        isEdit={isEdit}
        partValue={partValue}
      />
    </Form>
  );
};
